import { getAgentV2ReferenceDisplayName, getAgentV2ReferenceMetadataSummaryText } from '@/agent-v2/utils/agent-v2-reference-displays'
import { AgentV2UnavailableSource } from './AgentV2UnavailableSource'
import { AgentV2WebSourceContent } from './AgentV2WebSourceTooltip'
import { canOpenAgentV2Reference, getAgentV2ExternalReferenceUrl } from './agent-v2-reference-opening'
import { InlineTextButton } from '@/components/inline-text-button/InlineTextButton'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'
import { CaseSummaryTooltipContent } from '@/citator/components/CaseSummaryTooltipContent'
import { getAgentV2ReferenceLrrContentStatus } from './agent-v2-reference-lrr-status'
import { isDisplayableLrrContentStatus } from './lrr-content-status'
import { LrrContentStatusTag } from './LrrContentStatusTag'

/** How many saved docket lines the card previews; the panel shows them all. */
const kDocketPreviewLineCount = 3

type AgentV2CitationSourceTooltipContentProps = {
  reference: AgentV2Reference
  onViewReference: () => void
  onViewCitationAnalysis: () => void
  onDismiss?: () => void
}

/**
 * The body of a citation source hover tooltip: source title (click = view source),
 * Caselaw cards intentionally emphasize the title, generated summary, and citator treatment.
 * Other source cards may also show reference metadata and LRR content status; web and court
 * docket cards show their saved support (a docket's summary, first lines, and as-of note).
 *
 * One content for every citation trigger (numbered badge, legal citation link) so
 * what the user sees about a source never depends on which element they hovered.
 */
export function AgentV2CitationSourceTooltipContent(props: AgentV2CitationSourceTooltipContentProps) {
  const { reference, onViewReference, onViewCitationAnalysis, onDismiss } = props

  const title = getAgentV2ReferenceDisplayName(reference)
  if (reference.source_type === 'caselaw') {
    return (
      <CaseSummaryTooltipContent
        title={title}
        shortSummary={reference.short_summary}
        caseStatus={reference.case_status}
        onViewReference={onViewReference}
        onViewCitationAnalysis={onViewCitationAnalysis}
        onDismiss={onDismiss}
      />
    )
  }

  const metadataSummaryText = getAgentV2ReferenceMetadataSummaryText(reference)
  const lrrContentStatus = getAgentV2ReferenceLrrContentStatus(reference)
  const externalUrl = reference.source_type === 'administrative_decision' ? getAgentV2ExternalReferenceUrl(reference) : null
  if (externalUrl) return <AgentV2WebSourceContent source={{ url: externalUrl, title }} />
  if (reference.source_type === 'administrative_decision' && !canOpenAgentV2Reference(reference, { opensInPanel: true }))
    return <AgentV2UnavailableSource sourceUrl={reference.source.source_url} />

  return (
    // font-normal beats MUI's tooltip-wide 500 weight (the Figma spec's "kinda bold" note);
    // no vertical padding of its own — the bubble already carries the spec's 20px.
    <div className="max-w-xs xs:max-w-screen-xs sm:max-w-screen-sm text-sm leading-5 tracking-[-0.15px] font-normal text-brand-neutral-950">
      <div className="mt-1">
        <InlineTextButton onClick={onViewReference} className="compatible-word-break whitespace-pre-wrap">
          {title}
        </InlineTextButton>
      </div>

      {metadataSummaryText && <div className="mt-1 pr-1 text-sm font-normal text-brand-neutral-950 compatible-word-break">{metadataSummaryText}</div>}

      {reference.source_type === 'web' && (
        <div className="mt-2 text-sm font-normal text-brand-neutral-950 compatible-word-break">
          <p>{reference.support.summary}</p>
          <p className="mt-1 text-xs text-brand-neutral-600">Source summary, not a quotation. Opens the original website.</p>
        </div>
      )}

      {reference.source_type === 'docket' && (
        <div className="mt-2 text-sm font-normal text-brand-neutral-950 compatible-word-break">
          <p>{reference.support.summary}</p>
          {reference.support.selected_sentences.length > 0 && (
            <ul className="mt-1 list-none space-y-1 text-xs leading-4 text-brand-neutral-800">
              {reference.support.selected_sentences.slice(0, kDocketPreviewLineCount).map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-xs text-brand-neutral-600">
            {reference.source.provider_last_updated ? `Docket last updated ${reference.source.provider_last_updated}. ` : ''}
            Live court record, not legal authority.
          </p>
        </div>
      )}

      {isDisplayableLrrContentStatus(lrrContentStatus) && (
        <div className="mt-2 flex">
          <LrrContentStatusTag status={lrrContentStatus} />
        </div>
      )}
    </div>
  )
}
